import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import API from "../constants/API";
import { getTeamID, JSONParser } from "../common/function";
import { authHeader } from "../common/mainfunction";
import { GET_FEATURE_FLAGS_URL } from "../common/url";
import { FeatureFlags } from "../types/api";

export interface FeatureFlagContextValue extends FeatureFlags {
  refreshFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | Record<string, never>>({});

// Default feature flags (used as fallback if API fails)
const DEFAULT_FLAGS: FeatureFlags = {
  ebsSyncEnabled: true,
  ebsOrgId: null,
  supplierInviteDisabled: true,
  manualSupplierCreationDisabled: true,
  emailImportEnabled: false,
  physicalScannerEnabled: false,
  enableWorkflowRouting: false,
  supplierPortalEnabled: false,
  signupEnabled: false,
  mobileBlockerEnabled: true,
  approvalLimitsEnabled: false,
  ssoEnabled: false,
  jitAccessEnabled: false,
  oicSupplierSyncEnabled: false,
  oicExportEnabled: false,
  oicValidationEnabled: false,
  abnLookupEnabled: false,
  supplierDashboardEnabled: true,
  defaultCurrency: "USD",
  yardiExportEnabled: false,
  achPaymentEnabled: false,
  supplierDocumentsEnabled: false,
  barcodeSeparationEnabled: false,
  teamsIntegrationEnabled: false,
  slackIntegrationEnabled: false,
  i18nEnabled: false,
  defaultLanguage: "en",
  availableLanguages: ["en"],
  glSegmentsEnabled: false,
  budgetHolderRoutingEnabled: false,
  taxRegionEnabled: false,
  headerLineVarianceCheckEnabled: false,
  paymentTermsConversionEnabled: false,
  expenseModuleEnabled: false,
};

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

// ── Last-known-good flag persistence (per tenant) — UAT-1 ────────────────────
// The provider remounts on every full page reload (several workflow actions call
// window.location.reload()), and the dev API container is replaced in place
// (~10-20s LB outage). The old code fetched exactly ONCE at mount and swallowed
// failures, so a reload during an outage silently reverted the whole UI to the
// non-workflow defaults for the rest of the session. Flags must be sticky:
// a failed (re)fetch NEVER downgrades known-good flags — we hydrate the
// last-known-good set from sessionStorage and retry with backoff.
const FLAG_CACHE_PREFIX = "ezc.featureFlags.v1.";

const readCachedFlags = (teamId: string): Partial<FeatureFlags> | null => {
  try {
    const raw = sessionStorage.getItem(FLAG_CACHE_PREFIX + teamId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Partial<FeatureFlags>) : null;
  } catch {
    return null;
  }
};

const writeCachedFlags = (teamId: string, flags: FeatureFlags): void => {
  try {
    sessionStorage.setItem(FLAG_CACHE_PREFIX + teamId, JSON.stringify(flags));
  } catch {
    // Storage full/unavailable — non-fatal, flags still live in React state.
  }
};

// Backoff schedule: 2s, 4s, 8s, 16s, 30s, 30s (~90s coverage) — long enough to
// ride out an API container replacement.
const FLAG_FETCH_MAX_RETRIES = 6;
const FLAG_FETCH_RETRY_BASE_MS = 2000;
const FLAG_FETCH_RETRY_MAX_MS = 30000;

/**
 * Feature Flag Provider
 * Fetches feature flags from API on mount and provides them to children.
 * Prevents flash by not rendering children until flags are loaded (skipped when
 * last-known-good flags were hydrated from sessionStorage — no flash possible).
 * Failed fetches keep the current (last-known-good) flags and retry with backoff;
 * only a successful response ever updates flag state.
 */
export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const teamId = getTeamID();
    const cached = teamId ? readCachedFlags(teamId) : null;
    return cached ? { ...DEFAULT_FLAGS, ...cached } : DEFAULT_FLAGS;
  });
  // When hydrated from cache, render children immediately with the known-good
  // flags while the refetch runs in the background.
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    const teamId = getTeamID();
    return !(teamId && readCachedFlags(teamId));
  });
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);

  const attemptFetch = useCallback(async (attempt: number): Promise<void> => {
    const scheduleRetry = (reason: string): void => {
      if (!mountedRef.current) return;
      if (attempt + 1 >= FLAG_FETCH_MAX_RETRIES) {
        console.warn(
          `[FeatureFlags] ${reason} — giving up after ${attempt + 1} attempts; keeping last-known-good flags`
        );
        return;
      }
      const delay = Math.min(FLAG_FETCH_RETRY_MAX_MS, FLAG_FETCH_RETRY_BASE_MS * 2 ** attempt);
      console.warn(
        `[FeatureFlags] ${reason} — keeping last-known-good flags, retrying in ${delay}ms (attempt ${attempt + 1}/${FLAG_FETCH_MAX_RETRIES})`
      );
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        void attemptFetch(attempt + 1);
      }, delay);
    };

    try {
      const teamId = getTeamID();
      if (!teamId) {
        console.warn("[FeatureFlags] No teamId found, using defaults");
        return;
      }

      const config = {
        method: "GET",
        url: GET_FEATURE_FLAGS_URL,
        headers: authHeader(),
        params: { teamId },
      };

      const response = await API(config);
      const decrypted = response.data;
      const parsed = JSONParser(decrypted, { status: null, data: null });

      if (parsed.status === "Success" && parsed.data) {
        const flagData = parsed.data as Partial<FeatureFlags>;
        const merged: FeatureFlags = { ...DEFAULT_FLAGS, ...flagData };
        if (mountedRef.current) setFlags(merged);
        // Persist last-known-good so a remount during an API outage hydrates
        // these instead of reverting to DEFAULT_FLAGS.
        writeCachedFlags(teamId, merged);
      } else {
        scheduleRetry("API returned non-success");
      }
    } catch (error) {
      scheduleRetry(`fetch failed (${error instanceof Error ? error.message : String(error)})`);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  const fetchFeatureFlags = useCallback(async (): Promise<void> => {
    // A manual refresh supersedes any pending retry.
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    return attemptFetch(0);
  }, [attemptFetch]);

  useEffect(() => {
    mountedRef.current = true;
    fetchFeatureFlags();
    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [fetchFeatureFlags]);

  // Don't render children until flags are loaded
  // This prevents the "flash" of default UI before feature flags apply
  if (isLoading) return null;

  return (
    <FeatureFlagContext.Provider value={{ ...flags, refreshFlags: fetchFeatureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flags
 * @returns Feature flags object
 *
 * Usage:
 *   const { physicalScannerEnabled, emailImportEnabled } = useFeatureFlags();
 *
 *   {physicalScannerEnabled && <ScanButton />}
 */
export const useFeatureFlags = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context as FeatureFlagContextValue;
};

export default FeatureFlagContext;
